import { makeAutoObservable } from "mobx";
import { inject, injectable } from "tsyringe";
import { ProjectBusEvents, TOKEN_IProjectBus, type IProjectBus } from "@/services/ProjectBus";
import UserStore from "../transient/UserStore";
import { TOKEN_ITransientLocator, type ITransientLocator } from "@/services/TransientLocator";
import { ProjectUser } from "@/ProjectWorld";
import { EntityCollection } from "@utilitydelta/clientside-event-sourcing";
import { TOKEN_IProjectWorldReader, type IProjectWorldReader } from "@/services/ProjectWorldReader";
import { TOKEN_IUserIdentity, type IUserIdentity } from "@/services/UserIdentity";
import { TOKEN_IWrapTranslations, type IWrapTranslations } from "@/services/WrapTranslations";
import { TOKEN_INavigationRedirect, type INavigationRedirect } from "@/services/NavigationRedirect";
import { TOKEN_IProjectWorldWriter, type IProjectWorldWriter } from "@/services/ProjectWorldWriter";
import { TOKEN_ProjectAccessStore, ProjectAccessStore } from "../utility-stores/ProjectAccessStore";
import { TOKEN_IWrapAuth0, type IWrapAuth0 } from "@/services/WrapAuth0";

export const TOKEN_UsersDialogStore = Symbol("UsersDialogStore");

@injectable()
export class UsersDialogStore {
    leaveProject = async () => {
        if (!confirm(this.t.t('confirm_leave_project'))) return;

        await this.projectWorldWriter.deleteProjectWorld(this.projectAccessStore.currentProjectId!);
        await this.navigationRedirect.checkAndRedirect(true);
    }

    get me(): UserStore | undefined {
        return this._userStores.find(x => x.isMe);
    }

    get isOwner(): boolean {
        const me = this.me;
        if (!me) return false;
        return me.isOwner;
    }

    private _projectId: string | undefined;

    private _userIdMap: Map<string, UserStore> = new Map();
    private _userStores: UserStore[] = [];
    public get userStores(): UserStore[] {
        return this._userStores;
    }
    public get otherUsers(): UserStore[] {
        return this._userStores.filter(x => !x.isMe && !x.noAccess);
    }

    constructor(
        @inject(TOKEN_IProjectBus) projectBus: IProjectBus,
        @inject(TOKEN_ITransientLocator) private readonly transientLocator: ITransientLocator,
        @inject(TOKEN_IProjectWorldReader) private readonly projectWorldReader: IProjectWorldReader,
        @inject(TOKEN_IWrapTranslations) private t: IWrapTranslations,
        @inject(TOKEN_IUserIdentity) private readonly userIdentity: IUserIdentity,
        @inject(TOKEN_IWrapAuth0) private readonly wrapAuth0: IWrapAuth0,
        @inject(TOKEN_INavigationRedirect) private navigationRedirect: INavigationRedirect,
        @inject(TOKEN_IProjectWorldWriter) private projectWorldWriter: IProjectWorldWriter,
        @inject(TOKEN_ProjectAccessStore) private projectAccessStore: ProjectAccessStore,
    ) {
        makeAutoObservable(this);

        projectBus.subscribe_projectLoaded(this.onProjectLoaded);
        projectBus.subscribe_projectionUpdated(this.onProjectionUpdated);
    }

    private onProjectionUpdated = async (data: ProjectBusEvents['PROJECTION_UPDATED']): Promise<void> => {
        if (!this._projectId || !this._userStores) return;
        if (data.projectId !== this._projectId) return;

        if (data.mutationResult.kind === 'AssignRandomUsername_ProjectionResult') {
            //Users' display name might have changed
            const userStore = this.getOrCreateStore(data.mutationResult.projectUser);
            userStore.randomUsername = data.mutationResult.projectUser.randomUsername;
        }

        if (data.mutationResult.kind === 'SetColor_ProjectionResult') {
            //Users' color might have changed
            const id = data.mutationResult.square.modifiedByUserId ?? data.mutationResult.square.modifiedByClientId;
            if (id) {
                const userStore = this._userIdMap.get(id);
                if (userStore) {
                    userStore.lastColor = data.mutationResult.square.color;
                    userStore.lastSeenOn = data.mutationResult.square.modifiedOn;
                    this.reSortUsers();
                }
            }
        }

        if (data.mutationResult.kind === "UserVisit_ProjectionResult") {
            //Users' last seen might have changed
            const userStore = this.getOrCreateStore(data.mutationResult.projectUser);
            userStore.lastSeenOn = data.mutationResult.projectUser.lastSeenOn;
            this.reSortUsers();
        }

        if (data.mutationResult.kind === "ProvideAccess_ProjectionResult") {
            //Users' access might have changed - either granted, upgraded or removed access
            const userStore = this.getOrCreateStore(data.mutationResult.projectUser);
            userStore.accessLevel = data.mutationResult.projectUser.accessLevel;
            const myIdentity = this.wrapAuth0.userIdHashed ?? (await this.userIdentity.publicKey()).publicKeyHashed;
            userStore.isMe = myIdentity === data.mutationResult.projectUser.id;
        }
    }

    private reSortUsers() {
        this._userStores = this._userStores.sort((a, b) => {
            // Online users at the top, then sorted by lastSeenOnTimestamp descending
            // Online users at the top
            if (a.isOnline && !b.isOnline) return -1;
            if (!a.isOnline && b.isOnline) return 1;

            // Then sorted by lastSeenOnTimestamp descending
            return b.lastSeenOnTimestamp - a.lastSeenOnTimestamp;
        });
    }

    private getOrCreateStore(projectUser: ProjectUser): UserStore {
        let userStore = this._userIdMap.get(projectUser.id);
        if (userStore === undefined) {
            userStore = this.transientLocator.resolve<UserStore>(UserStore);
            userStore.init(projectUser);
            this._userStores.push(userStore);
            this._userIdMap.set(userStore.id, userStore);
        }
        return userStore;
    }

    private onProjectLoaded = async (data: ProjectBusEvents['PROJECT_LOADED']): Promise<void> => {
        if (this._projectId === data.projectId) return;
        this._projectId = data.projectId;
        const projectWorld = await this.projectWorldReader.mustGet(data.projectId);
        const myIdentity = this.wrapAuth0.userIdHashed ?? (await this.userIdentity.publicKey()).publicKeyHashed;
        this._userStores = this.buildUserStores(myIdentity, projectWorld.projectUsers);
        this._userIdMap = new Map(this._userStores.map(userStore => [userStore.id, userStore]));
    }

    private buildUserStores(myIdentity: string, projectUsers: EntityCollection<ProjectUser>): UserStore[] {
        return projectUsers.allNotDeleted.map(projectUser => {
            const store = this.transientLocator.resolve<UserStore>(UserStore);
            store.init(projectUser);
            store.isMe = myIdentity === projectUser.id;
            return store;
        });
    }

}